import { isUndefined, noop } from 'lodash-es';
import {
  useContext,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type TouchEvent as ReactTouchEvent,
} from 'react';

import { useAnnotationsSelectionState } from './store/selectors.js';
import {
  useSetStateSelection,
  useSetStateTooltip,
} from './store/state-setters.js';
import { useAnnotationsHandlers } from './useAnnotationsHandlers.js';
import { useMarkersInteractions } from './useMarkersInteractions.js';
import { TRACK_HEIGHT } from '../constants.js';
import { MarkerCoordinatesContext } from '../contexts/MarkerCoordinates.context.js';
import { initialState } from '../providers/AnnotationsDataInitialState.js';
import { inMarkerBounds } from '../utils/in-marker-bounds.js';

export const useOverlayEvents = () => {
  const markerCoordsPerTrack = useContext(MarkerCoordinatesContext);

  const {
    hoverTrack,
    blurTrack,
    hoverMarker,
    blurMarker,
    clickMarker,
    clearHovered,
    currentHoveredTrack,
    currentHoveredMarker,
  } = useAnnotationsHandlers();

  const { pinnedGroup } = useAnnotationsSelectionState();
  const updateTooltipState = useSetStateTooltip();
  const updateTooltipSelection = useSetStateSelection();
  const onMarkersInteraction = useMarkersInteractions();

  const handleMouseMove = (e: ReactMouseEvent) => {
    const { clientX, clientY, currentTarget } = e;
    const { top, left } = (currentTarget as Element).getBoundingClientRect();

    const mousePosition = {
      x: clientX - left,
      y: clientY - top,
    };

    let trackIndex = Math.max(0, Math.floor(mousePosition.y / TRACK_HEIGHT));

    if (trackIndex !== currentHoveredTrack.current) {
      hoverTrack(e, trackIndex);
    }

    if (markerCoordsPerTrack !== undefined && markerCoordsPerTrack.length > 0) {
      let isHoveringMarker = false;

      if (trackIndex >= markerCoordsPerTrack.length) {
        trackIndex = markerCoordsPerTrack.length - 1;
      }

      const markerCoords = markerCoordsPerTrack[trackIndex];

      // start from the end of the array to look first for the markers that are rendered on top
      for (
        let markerIndex = markerCoords.length - 1;
        markerIndex >= 0;
        markerIndex--
      ) {
        const marker = markerCoords[markerIndex];

        if (inMarkerBounds(mousePosition, trackIndex, marker)) {
          const markerId = `${trackIndex}_${marker.join('_')}`;

          hoverMarker(e, markerId);

          isHoveringMarker = true;
          break;
        }
      }

      // no marker found at the given mouse position
      if (!isHoveringMarker && currentHoveredMarker.current !== undefined) {
        blurMarker(currentHoveredMarker.current);
        // hover the track back when leaving the marker
        hoverTrack(e, trackIndex);
      }
    }
  };

  const handleClick = (e: ReactMouseEvent) => {
    if (currentHoveredMarker.current !== undefined) {
      clickMarker(e, currentHoveredMarker.current);
      return;
    }

    // Click landed inside the annotations track but not on a marker —
    // dismiss any pinned annotation tooltip.
    if (!isUndefined(pinnedGroup)) {
      updateTooltipState(initialState.tooltip);
      updateTooltipSelection(initialState.selection);
      onMarkersInteraction?.(initialState.selection);
    }
  };

  // On iOS, touchstart causes the toolbar to appear and the browser suppresses the
  // subsequent synthetic click. touchend fires before synthetic mousemove so
  // currentHoveredMarker is not yet set — hit detection is required here.
  // preventDefault() suppresses the synthetic click on subsequent taps to prevent
  // double-toggling the pin.
  const handleTouchEnd = (e: ReactTouchEvent<HTMLDivElement>) => {
    const touch = e.changedTouches[0];
    if (!touch || !markerCoordsPerTrack || markerCoordsPerTrack.length === 0) {
      return;
    }

    const { clientX, clientY } = touch;
    const { top, left } = (e.currentTarget as Element).getBoundingClientRect();
    const mousePosition = { x: clientX - left, y: clientY - top };

    let trackIndex = Math.max(0, Math.floor(mousePosition.y / TRACK_HEIGHT));
    if (trackIndex >= markerCoordsPerTrack.length) {
      trackIndex = markerCoordsPerTrack.length - 1;
    }

    const markerCoords = markerCoordsPerTrack[trackIndex];
    for (
      let markerIndex = markerCoords.length - 1;
      markerIndex >= 0;
      markerIndex--
    ) {
      const marker = markerCoords[markerIndex];
      if (inMarkerBounds(mousePosition, trackIndex, marker)) {
        if (e.cancelable) {
          e.preventDefault();
        }
        // stopPropagation is a no-op here: preventDefault() above already suppresses the synthetic click.
        clickMarker(
          { clientX, clientY, stopPropagation: noop },
          `${trackIndex}_${marker.join('_')}`,
        );
        return;
      }
    }
  };

  // Without this guard, pointerleave on finger-lift fires blurMarker before React
  // re-renders, hiding the tooltip that handleTouchEnd just pinned.
  // clearHovered() resets the tracked state so a subsequent mouse hover over the
  // same marker triggers onMouseEnter (visibility) rather than onMouseMove (position only).
  const handlePointerLeave = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'touch') {
      clearHovered();
      return;
    }

    if (currentHoveredTrack.current !== undefined) {
      blurTrack();
    }

    if (currentHoveredMarker.current !== undefined) {
      blurMarker(currentHoveredMarker.current);
    }
  };

  return {
    handleMouseMove,
    handleClick,
    handleTouchEnd,
    handlePointerLeave,
  };
};
